"use client"

import { useState, useEffect } from "react"
import { X, Calendar, Users2, Phone, User } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "./ui/button"
import { Label } from "./ui/label"
import { Input } from "./ui/input"
import { auth, db } from "../firebase"
import { ref, runTransaction } from "../lib/firebase-database"
import { useAuth } from "../hooks/useAuth"

const WhatsAppEnquiryForm = ({
  isOpen,
  onClose,
  villaName,
  whatsappNumber,
  checkInDate,
  checkOutDate,
  guestCount,
  onSubmit,
  villaId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    guests: guestCount || "",
    checkIn: checkInDate ? new Date(checkInDate).toISOString().split("T")[0] : "",
    checkOut: checkOutDate ? new Date(checkOutDate).toISOString().split("T")[0] : "",
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    if (checkInDate) {
      const dateStr = new Date(checkInDate).toISOString().split("T")[0]
      setFormData((prev) => ({ ...prev, checkIn: dateStr }))
    }
    if (checkOutDate) {
      const dateStr = new Date(checkOutDate).toISOString().split("T")[0]
      setFormData((prev) => ({ ...prev, checkOut: dateStr }))
    }
    if (guestCount) setFormData((prev) => ({ ...prev, guests: guestCount }))
  }, [checkInDate, checkOutDate, guestCount])

  // Format date to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return ""
    const parts = dateStr.split("-")
    return `${parts[2]}/${parts[1]}/${parts[0]}`
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) newErrors.name = "Name is required"
    if (!formData.phone.trim()) newErrors.phone = "Contact number is required"
    if (!formData.checkIn) newErrors.checkIn = "Check-in date is required"
    if (!formData.checkOut) newErrors.checkOut = "Check-out date is required"
    if (formData.guests < 1) newErrors.guests = "At least 1 guest is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    // Format the WhatsApp message
    const message = `
Hello! I just come from VillaKosh.in and I would like to book ${villaName} with the following details:

Name: ${formData.name}
Contact: ${formData.phone}
Check-in: ${formatDate(formData.checkIn)}
Check-out: ${formatDate(formData.checkOut)}
Guests: ${formData.guests}

Please confirm availability and provide booking details.
    `.trim()

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`

    // Notify parent component
    if (onSubmit) {
      onSubmit({
        name: formData.name,
        contactNumber: formData.phone,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        guests: formData.guests,
        message,
      })
    }

    // Only track analytics if user is logged in and villaId exists
    if (user && villaId) {
      const analyticsRef = ref(db, `analytics/whatsapp/${villaId}`)
      runTransaction(analyticsRef, (currentCount) => {
        return (currentCount || 0) + 1
      })
    }

    // Open WhatsApp
    window.open(whatsappUrl, "_blank")

    setIsSubmitting(false)
    onClose()
  }

  // Prevent background scrolling
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
      document.body.style.height = "100vh"
      document.body.style.touchAction = "none"
    } else {
      document.body.style.overflow = "unset"
      document.body.style.height = "auto"
      document.body.style.touchAction = "auto"
    }

    return () => {
      document.body.style.overflow = "unset"
      document.body.style.height = "auto"
      document.body.style.touchAction = "auto"
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="w-[90vw] max-w-[400px] my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-xl p-5 shadow-xl">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold">WhatsApp Enquiry</h2>
                <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">
                      Your Name
                    </Label>
                    <div className="mt-1 relative">
                      <User className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="pl-8 py-2 text-sm"
                        placeholder="Enter your name"
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">
                      Phone Number
                    </Label>
                    <div className="mt-1 relative">
                      <Phone className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        className="pl-8 py-2 text-sm"
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="checkIn" className="text-sm font-medium">
                        Check-in
                      </Label>
                      <div className="mt-1 relative">
                        <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          id="checkIn"
                          type="date"
                          required
                          min={new Date().toISOString().split("T")[0]}
                          value={formData.checkIn}
                          onChange={(e) => setFormData((prev) => ({ ...prev, checkIn: e.target.value }))}
                          className="pl-8 py-2 text-sm"
                          style={{
                            WebkitAppearance: "none",
                            MozAppearance: "none",
                          }}
                        />
                        {errors.checkIn && <p className="text-red-500 text-xs mt-1">{errors.checkIn}</p>}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="checkOut" className="text-sm font-medium">
                        Check-out
                      </Label>
                      <div className="mt-1 relative">
                        <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <Input
                          id="checkOut"
                          type="date"
                          required
                          min={formData.checkIn || new Date().toISOString().split("T")[0]}
                          value={formData.checkOut}
                          onChange={(e) => setFormData((prev) => ({ ...prev, checkOut: e.target.value }))}
                          className="pl-8 py-2 text-sm"
                          style={{
                            WebkitAppearance: "none",
                            MozAppearance: "none",
                          }}
                        />
                        {errors.checkOut && <p className="text-red-500 text-xs mt-1">{errors.checkOut}</p>}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="guests" className="text-sm font-medium">
                      Number of Guests
                    </Label>
                    <div className="mt-1 relative">
                      <Users2 className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="guests"
                        type="number"
                        min="1"
                        required
                        value={formData.guests}
                        onChange={(e) => setFormData((prev) => ({ ...prev, guests: e.target.value }))}
                        className="pl-8 py-2 text-sm"
                        placeholder="Enter number of guests"
                      />
                      {errors.guests && <p className="text-red-500 text-xs mt-1">{errors.guests}</p>}
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  </svg>
                  {isSubmitting ? "Sending..." : "Send WhatsApp Enquiry"}
                </Button>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default WhatsAppEnquiryForm